import { doc, getDocs, collection, setDoc, increment, serverTimestamp } from 'firebase/firestore'
import { db } from './firebase'
import { getUser } from './user'
import type { DayDoc, HistogramSummary, QuestionResult } from './types'
import { binOf, binCentre, scoreFor } from './scoring'

const SHARDS = 10

/** One write per player per day: bump this player's bins on a random shard. */
export async function submitResults(date: string, results: QuestionResult[], score: number): Promise<void> {
  const uid = getUser()?.uid
  if (uid) {
    // create-once marker; if it already exists this player has voted — skip the histogram write
    try { await setDoc(doc(db, 'days', date, 'votes', uid), { at: serverTimestamp() }) }
    catch { return }
  }
  const shard = String(Math.floor(Math.random() * SHARDS))
  // setDoc+merge with dotted keys creates nested maps on first write.
  const bucket = Math.min(20, Math.floor(score / 250))
  const nested: any = { players: increment(1), scores: { [bucket]: increment(1) }, q: {} }
  for (const r of results) { if (!r.timedOut) nested.q[r.id] = { [binOf(r.guess)]: increment(1) } }
  await setDoc(doc(db, 'days', date, 'shards', shard), nested, { merge: true })
}

export async function loadSummary(day: DayDoc): Promise<HistogramSummary> {
  if (day.summary) return day.summary
  const out: HistogramSummary = { q: {}, scores: {}, players: 0 }
  try {
    const snap = await getDocs(collection(db, 'days', day.date, 'shards'))
    for (const s of snap.docs) {
      const d = s.data()
      out.players += d.players ?? 0
      for (const [k, v] of Object.entries<number>(d.scores ?? {})) out.scores[k] = (out.scores[k] ?? 0) + v
      for (const [qid, bins] of Object.entries<Record<string, number>>(d.q ?? {})) {
        out.q[qid] ??= {}
        for (const [b, n] of Object.entries(bins)) out.q[qid][b] = (out.q[qid][b] ?? 0) + n
      }
    }
  } catch (e) {
    console.warn('loadSummary failed', e)
  }
  return out
}

/** % of players today whose score bucket is below yours (ties count half). Buckets are 250 points wide. */
export function percentileOf(score: number, scores: Record<string, number>): number | undefined {
  const mine = Math.min(20, Math.floor(score / 250))
  let below = 0, equal = 0, total = 0
  for (const [k, n] of Object.entries(scores)) {
    const b = Number(k); total += n
    if (b < mine) below += n; else if (b === mine) equal += n
  }
  // our own vote may not be in the summary yet (write races the read) — count ourselves exactly once
  if (equal === 0) { equal = 1; total += 1 }
  if (total < 2) return undefined
  const pct = ((below + (equal - 1) / 2) / (total - 1)) * 100
  return Math.min(100, Math.max(0, Math.round(pct)))
}

/** The chimp = the crowd's median bin per question. Returns how many questions you beat it on. */
export function chimpWins(results: QuestionResult[], q: HistogramSummary['q']): number | undefined {
  let wins = 0, counted = 0
  for (const r of results) {
    const bins = q[r.id]; if (!bins) continue
    const total = Object.values(bins).reduce((a, b) => a + b, 0)
    if (total < 3) continue
    let acc = 0, median = 0
    for (const b of Object.keys(bins).map(Number).sort((a, b) => a - b)) {
      acc += bins[b]; if (acc >= total / 2) { median = b; break }
    }
    const chimpScore = scoreFor(binCentre(median), r.answer)
    counted++
    if (r.qscore >= chimpScore) wins++
  }
  return counted ? wins : undefined
}
